import {
  CalendarOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CoffeeOutlined,
  EnvironmentOutlined,
  LoginOutlined,
  SwapOutlined,
} from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  App,
  Button,
  Card,
  Checkbox,
  Descriptions,
  Drawer,
  Empty,
  Form,
  Input,
  Modal,
  Space,
  Spin,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import type { TableProps } from "antd";
import type { TFunction } from "i18next";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiError, apiFetch } from "../api/client";
import { useServerNotifications } from "../api/notifications";
import type {
  AlternativeDateRow,
  CongregationArchiveRow,
  ContactPerson,
  ListResponse,
  NotificationsResponse,
  SpeakerEventInfoResponse,
  SpeakerEventRow,
  SubstitutionRequestRow,
} from "../api/types";
import { DataTable } from "../components/DataTable";
import type { ExportableColumn } from "../components/DataTable";

interface AvailabilityFormValues {
  available: boolean;
  additional_notes: string;
}

/** Which half of the detail panel is being read. */
type DetailTab = "info" | "substitutions";

/**
 * `ApplicationHelper::ORATORE_IN_VISITA` — the only `event_type` a substitute
 * can stand in for. Other event types (own congregation, zone, ...) have no
 * such notion, and the server's own `search_alternative_date` silently no-ops
 * for them rather than erroring, so the button has to be hidden here.
 */
const ORATORE_IN_VISITA = 30;

/**
 * Who is putting the speaker up, or why nobody is. The calendar cell, its
 * export and the detail panel all name the same four states, which the server
 * reports as `hospitality_status` on both endpoints.
 */
function hospitalityText(
  status: string | null | undefined,
  hostName: string | null | undefined,
  t: TFunction,
): string | null | undefined {
  if (!status) return null;
  if (status === "established") return hostName;
  if (status === "not_available") return t("local_assignation.hospitality_not_available");
  // Not a rejection — the speaker has not yet said whether they want to be
  // put up at all, so this is a prompt to decide, not a statement of fact.
  // (`hospitality_speaker_not_required` is a different message: the notice
  // shown to the *coordinator* when a host search hits a speaker who already
  // declined.)
  if (status === "speaker_no_accept") return t("local_assignation.hospitality_speaker_accept");
  return t("local_assignation.hospitality_not_established");
}

/** A row of a description list; empty values are left out entirely. */
function rows(entries: [string, string | number | null | undefined][]) {
  return entries
    .filter(([, value]) => value !== null && value !== undefined && value !== "")
    .map(([label, value]) => ({ key: label, label, children: value }));
}

/**
 * Sidebar entry "speaker_assignations" → /speaker_assignation/assignations.
 *
 * The speaker's own calendar. Every week is a row, and which actions it offers
 * is decided by the flags the server computes — not by this screen guessing
 * from the event type. The old toolbar offered all of them all the time and let
 * the server decline; showing only what applies says the same thing earlier.
 *
 * Four things can happen in a week:
 *  - declaring oneself unavailable (only where nothing is planned)
 *  - joining the congregation's or the zone's event one has been asked to
 *  - asking to be moved to another date, and picking which dates would suit
 *  - accepting or declining the hospitality the congregation offers
 */
export function SpeakerAssignationPage() {
  const { t } = useTranslation();
  const notify = useServerNotifications();
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<AvailabilityFormValues>();

  const [editing, setEditing] = useState<SpeakerEventRow | null>(null);
  // The panel the old screen kept beside the calendar, as a drawer: a row opens
  // it on the week's details, the substitution button on the two lists.
  const [detail, setDetail] = useState<{ row: SpeakerEventRow; tab: DetailTab } | null>(null);

  const events = useQuery({
    queryKey: ["speaker_assignation", "events"],
    queryFn: () => apiFetch<ListResponse<SpeakerEventRow>>("/speaker_assignation/events"),
  });

  const showError = useCallback(
    (error: unknown) => {
      if (error instanceof ApiError && error.notifications) {
        notify(error.notifications);
        return;
      }
      void message.error(error instanceof Error ? error.message : String(error));
    },
    [notify, message],
  );

  const refresh = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ["speaker_assignation"] });
    void queryClient.invalidateQueries({ queryKey: ["archive"] });
  }, [queryClient]);

  /** Every action here is one call against one week. */
  const act = useMutation({
    mutationFn: ({ path, method }: { path: string; method: "PUT" | "DELETE" }) =>
      apiFetch<NotificationsResponse>(path, { method }),
    onSuccess: (data) => {
      notify(data.notifications);
      refresh();
    },
    onError: showError,
  });

  const saveAvailability = useMutation({
    mutationFn: ({ week, values }: { week: string; values: AvailabilityFormValues }) =>
      apiFetch<NotificationsResponse>(`/speaker_assignation/events/${week}/availability`, {
        method: "PUT",
        body: values,
      }),
    onSuccess: (data) => {
      notify(data.notifications);
      setEditing(null);
      refresh();
    },
    onError: showError,
  });

  const busy = act.isPending || saveAvailability.isPending;

  const confirmThen = useCallback(
    (question: string, run: () => void) => {
      modal.confirm({
        title: t("dialog.confirmation_title"),
        content: question,
        okText: t("dialog.answer_yes"),
        okButtonProps: { danger: true },
        cancelText: t("dialog.answer_no"),
        onOk: run,
      });
    },
    [modal, t],
  );

  // Who is putting the speaker up, or why nobody is — the cell and the export
  // read the same two fields.
  const hospitality = useCallback(
    (row: SpeakerEventRow) => hospitalityText(row.hospitality_status, row.hospitality_host_name, t),
    [t],
  );

  const columns = useMemo<ExportableColumn<SpeakerEventRow>[]>(
    () => [
      {
        title: t("activerecord.attributes.local_assignation.week_begin"),
        dataIndex: "week_label",
        width: 190,
        fixed: "left",
        // The tags go under the date instead of beside it: side by side they
        // share the column's width and the date is what breaks.
        render: (value: string, row) => (
          <Space orientation="vertical" size={0} align="start">
            <span style={{ whiteSpace: "nowrap" }}>{value}</span>
            {(row.modified || row.available === false) && (
              <Space size={4} wrap>
                {row.modified && (
                  <Tag color="green" style={{ marginInlineEnd: 0 }}>
                    {t("icon.modified")}
                  </Tag>
                )}
                {row.available === false && (
                  <Tag color="orange" style={{ marginInlineEnd: 0 }}>
                    {t("speaker_assignation.not_available")}
                  </Tag>
                )}
              </Space>
            )}
          </Space>
        ),
        exportValue: (row) =>
          [
            row.week_label,
            row.modified ? t("icon.modified") : null,
            row.available === false ? t("speaker_assignation.not_available") : null,
          ]
            .filter(Boolean)
            .join(" · "),
      },
      {
        title: t("activerecord.attributes.local_assignation.event_type"),
        dataIndex: "event_type_label",
        width: 180,
        ellipsis: true,
      },
      {
        title: t("activerecord.attributes.local_assignation.week_day"),
        dataIndex: "week_day_label",
        width: 110,
      },
      {
        title: t("activerecord.attributes.local_assignation.day_time"),
        dataIndex: "day_time",
        width: 80,
      },
      {
        title: t("activerecord.models.congregation"),
        dataIndex: "congregation",
        width: 180,
        ellipsis: true,
      },
      {
        title: t("activerecord.attributes.speech.schema"),
        dataIndex: "speech_schema",
        width: 90,
        align: "right",
        render: (value: string | null, row) =>
          row.speech_inactive ? (
            <Tooltip title={t("speaker_assignation.inactive_schema")}>
              <Tag color="orange">{value}</Tag>
            </Tooltip>
          ) : (
            value
          ),
        exportValue: (row) =>
          row.speech_inactive
            ? `${row.speech_schema ?? ""} (${t("speaker_assignation.inactive_schema")})`
            : row.speech_schema,
      },
      {
        title: t("activerecord.attributes.speech.title"),
        dataIndex: "speech_title",
        width: 220,
        ellipsis: true,
      },
      {
        title: t("speaker_assignation.ribbon_substitution"),
        key: "substitution",
        width: 170,
        render: (_value, row) =>
          row.substitution_in_progress ? (
            <Tag color="blue">{t("speaker_assignation.subtitution_in_progress")}</Tag>
          ) : row.substitution_proposed ? (
            <Tag color="green">{t("speaker_assignation.substitution_proposed")}</Tag>
          ) : null,
        exportValue: (row) =>
          row.substitution_in_progress
            ? t("speaker_assignation.subtitution_in_progress")
            : row.substitution_proposed
              ? t("speaker_assignation.substitution_proposed")
              : null,
      },
      {
        title: t("activerecord.attributes.local_assignation.speaker_host_name"),
        key: "hospitality",
        width: 170,
        ellipsis: true,
        render: (_value, row) => hospitality(row),
        exportValue: (row) => hospitality(row),
      },
      {
        title: t("activerecord.attributes.local_assignation.additional_notes"),
        dataIndex: "additional_notes",
        ellipsis: true,
      },
      {
        key: "actions",
        fixed: "right",
        width: 170,
        render: (_value, row) => (
          <Space size={4}>
            {row.can_edit_availability && (
              <Tooltip title={t("speaker_assignation.available")}>
                <Button
                  size="small"
                  icon={<CalendarOutlined />}
                  disabled={busy}
                  onClick={(event) => {
                    event.stopPropagation();
                    setEditing(row);
                    form.resetFields();
                    form.setFieldsValue({
                      available: row.available !== false,
                      additional_notes: row.additional_notes ?? "",
                    });
                  }}
                />
              </Tooltip>
            )}

            {row.need_join_event && (
              <Tooltip title={t("speaker_assignation.join_event")}>
                <Button
                  size="small"
                  icon={<LoginOutlined />}
                  disabled={busy}
                  onClick={(event) => {
                    event.stopPropagation();
                    act.mutate({
                      path: `/speaker_assignation/events/${row.week_begin}/join`,
                      method: "PUT",
                    });
                  }}
                />
              </Tooltip>
            )}

            {/* A substitute only ever stands in for a visiting-speaker talk —
                see ORATORE_IN_VISITA above. */}
            {row.event_type === ORATORE_IN_VISITA &&
              (row.substitution_in_progress ? (
                <>
                  <Tooltip title={t("speaker_assignation_alternative.choose_alternatives")}>
                    <Button
                      size="small"
                      icon={<SwapOutlined />}
                      disabled={busy}
                      onClick={(event) => {
                        event.stopPropagation();
                        setDetail({ row, tab: "substitutions" });
                      }}
                    />
                  </Tooltip>
                  <Tooltip title={t("speaker_assignation.cancel_search_alternative")}>
                    <Button
                      size="small"
                      icon={<CloseCircleOutlined />}
                      disabled={busy}
                      onClick={(event) => {
                        event.stopPropagation();
                        act.mutate({
                          path: `/speaker_assignation/events/${row.week_begin}/alternative`,
                          method: "DELETE",
                        });
                      }}
                    />
                  </Tooltip>
                </>
              ) : (
                row.has_event && (
                  <Tooltip title={t("speaker_assignation.search_alternative")}>
                    <Button
                      size="small"
                      icon={<SwapOutlined />}
                      disabled={busy}
                      onClick={(event) => {
                        event.stopPropagation();
                        act.mutate({
                          path: `/speaker_assignation/events/${row.week_begin}/search_alternative`,
                          method: "PUT",
                        });
                      }}
                    />
                  </Tooltip>
                )
              ))}

            {/* An actual host's invitation to answer — "not_established" and
                "speaker_no_accept" are earlier states with no invitation yet
                to accept or decline. */}
            {row.hospitality_status === "established" && (
              <>
                <Tooltip title={t("speaker_assignation.accept_hospitality")}>
                  <Button
                    size="small"
                    icon={<CoffeeOutlined />}
                    disabled={busy}
                    onClick={(event) => {
                      event.stopPropagation();
                      act.mutate({
                        path: `/speaker_assignation/events/${row.week_begin}/hospitality/accept`,
                        method: "PUT",
                      });
                    }}
                  />
                </Tooltip>
                <Tooltip title={t("speaker_assignation.decline_hospitality")}>
                  <Button
                    size="small"
                    icon={<CloseCircleOutlined />}
                    disabled={busy}
                    onClick={(event) => {
                      event.stopPropagation();
                      act.mutate({
                        path: `/speaker_assignation/events/${row.week_begin}/hospitality`,
                        method: "DELETE",
                      });
                    }}
                  />
                </Tooltip>
              </>
            )}

            {/* Offered for any planned week, as the old toolbar did: whether it
                applies is the server's call, and it says so in a notice. */}
            {row.has_event && (
              <Tooltip title={t("speaker_assignation.cancel")}>
                <Button
                  size="small"
                  danger
                  icon={<CloseCircleOutlined />}
                  disabled={busy}
                  onClick={(event) => {
                    event.stopPropagation();
                    // A visiting-speaker talk needs the coordinator contacted
                    // personally to unwind hospitality/travel; any other event
                    // is a plain cancellation. See ORATORE_IN_VISITA above.
                    const confirmKey =
                      row.event_type === ORATORE_IN_VISITA
                        ? "speaker_assignation.confirm_cancel_speaker_assignation"
                        : "speaker_assignation.confirm_cancel_speaker_event";
                    confirmThen(t(confirmKey), () =>
                      act.mutate({
                        path: `/speaker_assignation/events/${row.week_begin}/cancel`,
                        method: "PUT",
                      }),
                    );
                  }}
                />
              </Tooltip>
            )}
          </Space>
        ),
      },
    ],
    [t, busy, act, form, confirmThen, hospitality],
  );

  return (
    <Card title={t("navigation.speaker_assignations")}>
      <DataTable<SpeakerEventRow>
        columns={columns}
        rows={events.data?.data}
        exportAs={t("navigation.speaker_assignations")}
        loading={events.isPending || busy}
        error={events.error}
        rowKey="week_begin"
        rowClassName={(row) => (row.has_event ? "" : "speaker-empty-week")}
        // The old screen filled the panel beside the calendar from whichever
        // row was selected; here the row opens it.
        onRowClick={(row) => setDetail({ row, tab: "info" })}
      />

      <style>{`.speaker-empty-week { opacity: 0.6; }`}</style>

      <Modal
        open={editing !== null}
        title={editing?.week_label}
        onCancel={() => setEditing(null)}
        okText={t("form.update")}
        okButtonProps={{ loading: saveAvailability.isPending }}
        onOk={() => form.submit()}
        destroyOnHidden
      >
        <Form<AvailabilityFormValues>
          form={form}
          layout="vertical"
          onFinish={(values) =>
            saveAvailability.mutate({ week: editing!.week_begin, values })
          }
        >
          <Typography.Paragraph type="secondary">
            {t("speaker_assignation.not_available_check")}
          </Typography.Paragraph>

          <Form.Item name="available" valuePropName="checked">
            <Checkbox>{t("speaker_assignation.available")}</Checkbox>
          </Form.Item>

          <Form.Item
            name="additional_notes"
            label={t("activerecord.attributes.local_assignation.additional_notes")}
          >
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>

      {detail && (
        <EventDetailDrawer
          event={detail.row}
          tab={detail.tab}
          onTab={(tab) => setDetail({ row: detail.row, tab })}
          onClose={() => setDetail(null)}
          onChanged={refresh}
        />
      )}
    </Card>
  );
}

/**
 * Everything about one week, which the old screen kept in the panel to the
 * right of the calendar: the details on one tab (info_window_speaker) and the
 * two substitution lists on the other (the alternatives grid and the
 * substitution requests grid). A drawer rather than a fixed panel — the
 * calendar is wide, and the details are read one week at a time.
 */
function EventDetailDrawer({
  event,
  tab,
  onTab,
  onClose,
  onChanged,
}: {
  event: SpeakerEventRow;
  tab: DetailTab;
  onTab: (tab: DetailTab) => void;
  onClose: () => void;
  onChanged: () => void;
}) {
  const { t } = useTranslation();

  return (
    <Drawer
      open
      onClose={onClose}
      size={880}
      destroyOnHidden
      title={[event.week_label, event.congregation].filter(Boolean).join(" · ")}
    >
      <Tabs
        activeKey={tab}
        onChange={(key) => onTab(key as DetailTab)}
        items={[
          {
            key: "info",
            label: t("speaker.speaker_events"),
            children: <EventDetails event={event} />,
          },
          {
            key: "substitutions",
            label: t("local_assignation.substitutions"),
            children: <SubstitutionPanel event={event} onChanged={onChanged} />,
          },
        ]}
      />
    </Drawer>
  );
}

/**
 * The week itself: what to weigh before the date, when and where it is, the
 * talk that was asked for, who is putting the speaker up, and who to call about
 * any of it. One request answers all of it — the distance in it costs a call to
 * a routing service, so it is only asked once a week is opened.
 */
function EventDetails({ event }: { event: SpeakerEventRow }) {
  const { t } = useTranslation();

  const info = useQuery({
    queryKey: ["speaker_assignation", "info", event.week_begin],
    queryFn: () =>
      apiFetch<SpeakerEventInfoResponse>(`/speaker_assignation/events/${event.week_begin}/info`),
  });

  if (info.isPending) return <Spin style={{ marginTop: 48 }} />;

  if (info.error) {
    return (
      <Alert
        type="error"
        showIcon
        title={info.error instanceof Error ? info.error.message : String(info.error)}
      />
    );
  }

  const data = info.data?.data;

  if (!data) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={t("events.no_events")}
        style={{ marginTop: 48 }}
      />
    );
  }

  return (
    <Space orientation="vertical" size="middle" style={{ width: "100%" }}>
      {/* The red lines of the old panel: what the speaker may want to take up
          with the coordinator before the date. */}
      {data.considerations.map((item) => (
        <Alert key={item.key} type="warning" showIcon title={item.title} description={item.text} />
      ))}

      <Descriptions
        size="small"
        column={1}
        title={data.event.event_type_label}
        items={rows([
          [t("week.header"), data.event.week_label],
          [t("day_date"), data.event.day_date],
          [t("activerecord.attributes.local_assignation.week_day"), data.event.week_day_label],
          [t("activerecord.attributes.local_assignation.day_time"), data.event.day_time],
          [
            t("activerecord.attributes.speaker_assignation.available"),
            data.event.available === false ? t("no") : t("yes"),
          ],
          [
            t("activerecord.attributes.local_assignation.additional_notes"),
            data.event.additional_notes,
          ],
        ])}
      />

      {data.speech && (
        <Descriptions
          size="small"
          column={1}
          title={t("activerecord.models.speech")}
          items={rows([
            [t("activerecord.attributes.speech.schema"), data.speech.schema],
            [t("activerecord.attributes.speech.title"), data.speech.title],
            [t("activerecord.models.language"), data.speech.language_code],
            [
              t("activerecord.attributes.speaker_speech.additional_notes"),
              data.speech.additional_notes,
            ],
          ])}
        />
      )}

      {data.hospitality && (
        <Descriptions
          size="small"
          column={1}
          title={t("speaker.headers.course_of_hospitality")}
          items={
            data.hospitality.status === "established"
              ? rows([
                  [
                    t("activerecord.attributes.local_assignation.speaker_host_name"),
                    data.hospitality.host_name,
                  ],
                  [
                    t("activerecord.attributes.local_assignation.speaker_host_email"),
                    data.hospitality.host_email,
                  ],
                  [
                    t("activerecord.attributes.local_assignation.speaker_host_phone"),
                    data.hospitality.host_phone,
                  ],
                  [
                    t("activerecord.attributes.local_assignation.speaker_host_address"),
                    data.hospitality.host_address,
                  ],
                ])
              : rows([
                  [
                    t("local_assignation.host"),
                    hospitalityText(data.hospitality.status, null, t),
                  ],
                ])
          }
        />
      )}

      {data.congregation && (
        <Descriptions
          size="small"
          column={1}
          title={t("activerecord.models.congregation")}
          items={rows([
            [t("activerecord.attributes.congregation.denomination"), data.congregation.denomination],
            [
              t("activerecord.attributes.congregation.congregation_code"),
              data.congregation.congregation_code,
            ],
            [t("activerecord.models.circumscription"), data.congregation.circumscription_code],
            [
              t("activerecord.attributes.congregation.congregation_phone"),
              data.congregation.congregation_phone,
            ],
            [
              t("congregation.headers.contact_person"),
              [data.congregation.contact_first_name, data.congregation.contact_last_name]
                .filter(Boolean)
                .join(" "),
            ],
            [t("activerecord.attributes.congregation.phone"), data.congregation.phone],
            [t("activerecord.attributes.congregation.mobile"), data.congregation.mobile],
            [t("activerecord.attributes.congregation.email"), data.congregation.email],
            [
              t("activerecord.attributes.congregation.street_number"),
              data.congregation.street_number,
            ],
            [t("activerecord.attributes.congregation.zip_code"), data.congregation.zip_code],
            [t("activerecord.attributes.congregation.city"), data.congregation.city],
            [t("activerecord.attributes.congregation.state"), data.congregation.state],
            [t("activerecord.attributes.congregation.country"), data.congregation.country],
            [t("link.distance"), data.distance === "?" ? null : data.distance],
            [t("link.drive_time"), data.distance === "?" ? null : data.drive_time],
          ])}
          extra={
            data.route_url ? (
              <Typography.Link href={data.route_url} target="_blank" rel="noreferrer">
                <Space size={4}>
                  <EnvironmentOutlined />
                  {t("link.googlemap")}
                </Space>
              </Typography.Link>
            ) : null
          }
        />
      )}

      {data.circumscription && (
        <Descriptions
          size="small"
          column={1}
          title={t("activerecord.models.circumscription")}
          items={rows([
            [
              t("activerecord.attributes.circumscription.denomination"),
              data.circumscription.denomination,
            ],
            [
              t("activerecord.attributes.circumscription.circumscription_code"),
              data.circumscription.circumscription_code,
            ],
            [t("activerecord.attributes.circumscription.country"), data.circumscription.country],
            [
              t("congregation.headers.contact_person"),
              [data.circumscription.contact_first_name, data.circumscription.contact_last_name]
                .filter(Boolean)
                .join(" "),
            ],
            [t("activerecord.attributes.circumscription.phone"), data.circumscription.phone],
            [t("activerecord.attributes.circumscription.mobile"), data.circumscription.mobile],
            [t("activerecord.attributes.circumscription.email"), data.circumscription.email],
          ])}
        />
      )}

      {data.coordinators.map((person, index) => (
        <ContactCard
          key={`lc-${index}`}
          person={person}
          title={t("activerecord.models.local_coordinator")}
        />
      ))}

      {data.zone_coordinators.map((person, index) => (
        <ContactCard
          key={`zc-${index}`}
          person={person}
          title={t("activerecord.models.zone_coordinator")}
        />
      ))}
    </Space>
  );
}

/** One coordinator and the contact data their account carries. */
function ContactCard({ person, title }: { person: ContactPerson; title: string }) {
  const { t } = useTranslation();

  return (
    <Descriptions
      size="small"
      column={1}
      title={title}
      items={rows([
        [
          t("activerecord.attributes.local_coordinator.first_name"),
          [person.first_name, person.last_name].filter(Boolean).join(" "),
        ],
        [t("activerecord.attributes.user.zip_code"), person.zip_code],
        [t("activerecord.attributes.user.country"), person.country],
        [t("activerecord.attributes.user.phone"), person.phone],
        [t("activerecord.attributes.user.mobile"), person.mobile],
        [t("activerecord.attributes.user.private_email"), person.private_email],
      ])}
    />
  );
}

/**
 * Both halves of the substitution panel. They read the same way round as the
 * old screen: first the dates found for a move this speaker asked for, then the
 * weeks another speaker asked to be moved out of — those can be taken over,
 * which swaps the two dates.
 */
function SubstitutionPanel({
  event,
  onChanged,
}: {
  event: SpeakerEventRow;
  onChanged: () => void;
}) {
  const { t } = useTranslation();

  return (
    <Space orientation="vertical" size="middle" style={{ width: "100%" }}>
      <Typography.Title level={5} style={{ marginBottom: 0 }}>
        {t("speaker_assignation.substitutions_found")}
      </Typography.Title>
      <AlternativesList event={event} onChanged={onChanged} />

      <Typography.Title level={5} style={{ marginBottom: 0 }}>
        {t("speaker_assignation.substitutions_proposed")}
      </Typography.Title>
      <SubstitutionRequestsList event={event} onChanged={onChanged} />
    </Space>
  );
}

/**
 * The dates that would suit instead. Two sources feed the same list — a swap
 * with another speaker's week, and the speaker's own free dates — and ticking
 * one records it as a preference for the coordinator to act on.
 */
function AlternativesList({
  event,
  onChanged,
}: {
  event: SpeakerEventRow;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const notify = useServerNotifications();
  const { message } = App.useApp();
  const queryClient = useQueryClient();

  const queryKey = useMemo(
    () => ["speaker_assignation", "alternatives", event.week_begin],
    [event.week_begin],
  );

  const alternatives = useQuery({
    queryKey,
    queryFn: () =>
      apiFetch<ListResponse<AlternativeDateRow>>(
        `/speaker_assignation/events/${event.week_begin}/alternatives`,
      ),
  });

  const prefer = useMutation({
    mutationFn: ({ row, preferred }: { row: AlternativeDateRow; preferred: boolean }) =>
      apiFetch<NotificationsResponse>(
        `/speaker_assignation/events/${event.week_begin}/alternatives/${row.week_begin}`,
        { method: "PUT", body: { preferred } },
      ),
    onSuccess: (data) => {
      notify(data.notifications);
      void queryClient.invalidateQueries({ queryKey });
      onChanged();
    },
    onError: (error: unknown) => {
      if (error instanceof ApiError && error.notifications) {
        notify(error.notifications);
        return;
      }
      void message.error(error instanceof Error ? error.message : String(error));
    },
  });

  const columns = useMemo<ExportableColumn<AlternativeDateRow>[]>(
    () => [
      {
        title: t("speaker_assignation_alternative.choose_alternatives"),
        dataIndex: "preferred",
        width: 220,
        render: (value: boolean, row) => (
          <Checkbox
            checked={value}
            disabled={prefer.isPending}
            onChange={(change) => prefer.mutate({ row, preferred: change.target.checked })}
          />
        ),
        // On paper the column is not something to tick but something that is
        // ticked, and "Alternativen wählen" would read as an instruction.
        exportHeader: t("activerecord.attributes.speaker_assignation_alternative.preference"),
      },
      {
        title: t("activerecord.attributes.local_assignation.week_begin"),
        // Already formatted with the congregation's meeting day by the server.
        dataIndex: "alternative_date",
        width: 220,
      },
      {
        title: t("activerecord.attributes.local_assignation.day_time"),
        dataIndex: "time",
        width: 100,
      },
    ],
    [t, prefer],
  );

  return (
    <DataTable<AlternativeDateRow>
      columns={columns}
      rows={alternatives.data?.data}
      loading={alternatives.isPending || prefer.isPending}
      error={alternatives.error}
      rowKey="week_begin"
      paginated={false}
      // The week comes second so that the worksheet name, which Excel cuts at
      // 31 characters, keeps the part that says which list this is.
      exportAs={[t("speaker_assignation.substitutions_found"), event.week_label]
        .filter(Boolean)
        .join(" · ")}
    />
  );
}

/**
 * The weeks of the same congregation whose speaker asked to be moved. Accepting
 * one swaps the two dates — the speaker takes that week, the other speaker
 * takes this one — which is what the button on each row does.
 *
 * The calendar already marks such a week with "Vorschlag für den Austausch";
 * this is the list behind that mark.
 */
function SubstitutionRequestsList({
  event,
  onChanged,
}: {
  event: SpeakerEventRow;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const notify = useServerNotifications();
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();

  const queryKey = useMemo(
    () => ["speaker_assignation", "substitution_requests", event.week_begin],
    [event.week_begin],
  );

  const requests = useQuery({
    queryKey,
    queryFn: () =>
      apiFetch<ListResponse<SubstitutionRequestRow>>(
        `/speaker_assignation/events/${event.week_begin}/substitution_requests`,
      ),
  });

  const accept = useMutation({
    mutationFn: (row: SubstitutionRequestRow) =>
      apiFetch<NotificationsResponse>(
        `/speaker_assignation/events/${event.week_begin}/substitution_requests/${row.local_assignation_id}/accept`,
        { method: "PUT" },
      ),
    onSuccess: (data) => {
      notify(data.notifications);
      void queryClient.invalidateQueries({ queryKey });
      onChanged();
    },
    onError: (error: unknown) => {
      if (error instanceof ApiError && error.notifications) {
        notify(error.notifications);
        return;
      }
      void message.error(error instanceof Error ? error.message : String(error));
    },
  });

  const columns = useMemo<TableProps<SubstitutionRequestRow>["columns"]>(
    () => [
      {
        title: t("activerecord.attributes.local_assignation.week_begin"),
        dataIndex: "week_label",
        width: 220,
      },
      {
        title: t("activerecord.attributes.local_assignation.week_day"),
        dataIndex: "day_label",
        width: 110,
      },
      {
        title: t("activerecord.attributes.local_assignation.day_time"),
        dataIndex: "day_time",
        width: 90,
      },
      {
        title: t("activerecord.attributes.speaker_assignation_alternative.preference"),
        dataIndex: "preferred",
        width: 110,
        align: "center",
        // The other speaker named this week themselves, so the swap is the one
        // both sides are waiting for.
        render: (value: boolean) =>
          value ? <CheckCircleOutlined style={{ color: "#389e0d" }} /> : null,
      },
      {
        key: "actions",
        width: 60,
        align: "right",
        render: (_value, row) => (
          <Tooltip title={t("speaker_assignation_alternative.accept_substitution")}>
            <Button
              size="small"
              type="primary"
              icon={<SwapOutlined />}
              loading={accept.isPending}
              onClick={() =>
                modal.confirm({
                  title: t("dialog.confirmation_title"),
                  content: [row.week_label, row.day_time].filter(Boolean).join(" · "),
                  okText: t("dialog.answer_yes"),
                  cancelText: t("dialog.answer_no"),
                  onOk: () => accept.mutate(row),
                })
              }
            />
          </Tooltip>
        ),
      },
    ],
    [t, accept, modal],
  );

  return (
    <DataTable<SubstitutionRequestRow>
      columns={columns}
      rows={requests.data?.data}
      loading={requests.isPending || accept.isPending}
      error={requests.error}
      rowKey="local_assignation_id"
      paginated={false}
      exportAs={[t("speaker_assignation.substitutions_proposed"), event.week_label]
        .filter(Boolean)
        .join(" · ")}
    />
  );
}

/**
 * Sidebar entry "speaker_congregation_assignations" →
 * /speaker_assignation/congregation_assignations.
 *
 * The calendar of the speaker's *own* congregation, read-only — what is planned
 * at home while they are out. Same rows the local coordinator edits, and the
 * same the congregation archive shows.
 */
export function SpeakerCongregationEventsPage() {
  const { t } = useTranslation();

  const events = useQuery({
    queryKey: ["speaker_assignation", "congregation_events"],
    queryFn: () =>
      apiFetch<ListResponse<CongregationArchiveRow>>(
        "/speaker_assignation/congregation_events",
      ),
  });

  const columns = useMemo<TableProps<CongregationArchiveRow>["columns"]>(
    () => [
      {
        title: t("activerecord.attributes.local_assignation.week_begin"),
        dataIndex: "week_label",
        width: 190,
        fixed: "left",
      },
      {
        title: t("activerecord.attributes.local_assignation.week_day"),
        dataIndex: "week_day_label",
        width: 110,
      },
      {
        title: t("activerecord.attributes.local_assignation.day_time"),
        dataIndex: "day_time",
        width: 80,
      },
      {
        title: t("activerecord.attributes.local_assignation.event_type"),
        dataIndex: "event_type_label",
        width: 180,
        ellipsis: true,
      },
      {
        title: t("activerecord.attributes.local_assignation.speaker_id"),
        dataIndex: "speaker_name",
        width: 180,
        ellipsis: true,
      },
      {
        title: t("activerecord.models.congregation"),
        dataIndex: "speaker_congregation",
        width: 170,
        ellipsis: true,
      },
      {
        title: t("activerecord.attributes.speech.schema"),
        dataIndex: "speech_schema",
        width: 90,
        align: "right",
      },
      {
        title: t("activerecord.attributes.speech.title"),
        dataIndex: "speech_title",
        ellipsis: true,
      },
      {
        title: t("activerecord.attributes.local_assignation.additional_notes"),
        dataIndex: "additional_notes",
        width: 200,
        ellipsis: true,
      },
    ],
    [t],
  );

  return (
    <Card title={t("navigation.speaker_congregation_assignations")}>
      <DataTable<CongregationArchiveRow>
        columns={columns}
        rows={events.data?.data}
        exportAs={t("navigation.speaker_congregation_assignations")}
        loading={events.isPending}
        error={events.error}
        rowKey="week_begin"
      />
    </Card>
  );
}
